// src/pages/Enseignants/PrepaPhysique/ExerciseAssignmentSection.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  getAllAdherentExerciseResults,
  deleteAdherentExerciseResult,
} from "../../../services/prepaPhysiqueServices";
import { toast } from "react-toastify";
import Button from "../../../components/Button/Button";
import ButtonEdit from "../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../components/Button/ButtonDelete";
import {
  FaPlus,
  FaSearch,
  FaExternalLinkAlt,
  FaCheck,
  FaTimes as FaTimesIcon,
} from "react-icons/fa";
import CreateExerciseResultModal from "./CreateExerciseResultModal";
import EditExerciseResultModal from "./EditExerciseResultModal";

const ExerciseAssignmentSection = () => {
  const [assignments, setAssignments] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);

  // Filtres
  const [search, setSearch] = useState("");
  const [programmeFilter, setProgrammeFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All"); // All | done | notdone

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const data = await getAllAdherentExerciseResults();
      setAssignments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur récupération affectations :", error);
      toast.error("Erreur lors de la récupération des affectations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssignments();
  }, []);

  // Programmes distincts pour le filtre
  const programmes = useMemo(() => {
    const setProg = new Set(
      (assignments || [])
        .map((a) => a?.Exercice?.programme)
        .filter((p) => p && String(p).trim().length > 0)
    );
    return ["All", ...Array.from(setProg).sort((a, b) => a.localeCompare(b))];
  }, [assignments]);

  // Filtrage
  useEffect(() => {
    const q = search.trim().toLowerCase();

    const list = (assignments || []).filter((it) => {
      const fullName = `${it?.Adherent?.prenom || ""} ${it?.Adherent?.nom || ""}`.trim();
      const exName = it?.Exercice?.nom || "";
      const prog = it?.Exercice?.programme || "";
      const isDone = !!it?.is_done;

      const inSearch =
        q.length === 0 ||
        [fullName, exName, prog]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));

      const inProgramme = programmeFilter === "All" || prog === programmeFilter;

      const inStatus =
        statusFilter === "All" ||
        (statusFilter === "done" && isDone) ||
        (statusFilter === "notdone" && !isDone);

      return inSearch && inProgramme && inStatus;
    });

    setFiltered(list);
  }, [assignments, search, programmeFilter, statusFilter]);

  // Actions
  const handleEditResult = (item) => {
    setSelectedResult(item);
    setIsEditModalOpen(true);
  };

  const handleDeleteResult = async (item) => {
    if (
      window.confirm(
        `Supprimer l'exercice "${item?.Exercice?.nom}" de ${item?.Adherent?.prenom} ${item?.Adherent?.nom} ?`
      )
    ) {
      try {
        await deleteAdherentExerciseResult(item.adherent_id, item.prepa_physique_id);
        toast.success("Exercice supprimé avec succès.");
        fetchAssignments();
      } catch (error) {
        toast.error("Erreur lors de la suppression.");
      }
    }
  };

  // UI utils
  const StatusPill = ({ done }) => (
    <span
      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ring-1 ${
        done
          ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:ring-emerald-800/60"
          : "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:ring-rose-800/60"
      }`}
    >
      {done ? <FaCheck /> : <FaTimesIcon />}
      {done ? "Fait" : "Non fait"}
    </span>
  );

  return (
    <div className="mx-auto w-full max-w-7xl px-3 sm:px-6 transition-all duration-300">
      {/* Carte principale */}
      <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-6">
        {/* Actions en-tête */}
        <div className="mt-5 flex items-center justify-center">
          <Button
            text="Assigner un exercice"
            icon={FaPlus}
            onClick={() => setIsCreateModalOpen(true)}
            className="!bg-white !text-slate-900 !hover:bg-white/90 !rounded-xl !shadow-md gap-1"
          />
        </div>

        {/* Filtres */}
        <div className="px-5 py-5 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            {/* Recherche */}
            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Recherche (adhérent / exercice / programme)
              </label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Ex : Dupont, Pompes…"
                  className="w-full rounded-xl pl-9 pr-3 py-2 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>
            </div>

            {/* Programme */}
            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Programme
              </label>
              <select
                value={programmeFilter}
                onChange={(e) => setProgrammeFilter(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                {programmes.map((p) => (
                  <option key={p} value={p}>
                    {p === "All" ? "Tous" : p}
                  </option>
                ))}
              </select>
            </div>

            {/* Statut */}
            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Statut
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="All">Tous</option>
                <option value="done">Fait</option>
                <option value="notdone">Non fait</option>
              </select>
            </div>
          </div>

          {/* Compteur */}
          <div className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-200">
            Affectations affichées : {filtered.length} / {assignments.length}
          </div>
        </div>

        {/* Liste : Table desktop / Cards mobile */}
        <div className="px-5 pb-6 sm:px-8">
          {loading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-24"
                />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-600 dark:text-slate-400">
              Aucune affectation ne correspond à vos filtres.
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white/90 dark:bg-slate-900/40">
                <table className="min-w-[960px] w-full text-sm">
                  <thead className="bg-slate-50/90 dark:bg-slate-900/70">
                    <tr className="text-left text-slate-700 dark:text-slate-200">
                      <th className="px-4 py-3">Adhérent</th>
                      <th className="px-4 py-3">Exercice</th>
                      <th className="px-4 py-3">Programme</th>
                      <th className="px-4 py-3">Répétitions</th>
                      <th className="px-4 py-3">Statut</th>
                      <th className="px-4 py-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {filtered.map((item) => (
                      <tr
                        key={`${item.adherent_id}-${item.prepa_physique_id}`}
                        className="bg-white/90 dark:bg-slate-900/40 hover:bg-slate-50 dark:hover:bg-slate-900/60 transition-colors"
                      >
                        <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                          {(item?.Adherent?.prenom || "—") +
                            " " +
                            (item?.Adherent?.nom || "")}
                        </td>
                        <td className="px-4 py-3">{item?.Exercice?.nom || "—"}</td>
                        <td className="px-4 py-3">{item?.Exercice?.programme || "—"}</td>
                        <td className="px-4 py-3">{item?.repetitions ?? "—"}</td>
                        <td className="px-4 py-3">
                          <StatusPill done={!!item?.is_done} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-center items-center gap-2">
                            <ButtonEdit onClick={() => handleEditResult(item)} />
                            <ButtonDelete onClick={() => handleDeleteResult(item)} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3">
                {filtered.map((item) => (
                  <div
                    key={`${item.adherent_id}-${item.prepa_physique_id}`}
                    className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-white/90 dark:bg-slate-900/40"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 dark:text-slate-100 truncate">
                          {(item?.Adherent?.prenom || "—") +
                            " " +
                            (item?.Adherent?.nom || "")}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          {item?.Exercice?.nom || "—"}
                          {item?.Exercice?.programme && (
                            <>
                              {" "}
                              • <span>{item.Exercice.programme}</span>
                            </>
                          )}
                        </div>
                        {item?.repetitions != null && (
                          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                            Répétitions : <span className="font-medium">{item.repetitions}</span>
                          </div>
                        )}
                      </div>
                      <StatusPill done={!!item?.is_done} />
                    </div>

                    <div className="mt-3 flex justify-end gap-2">
                      <ButtonEdit onClick={() => handleEditResult(item)} />
                      <ButtonDelete onClick={() => handleDeleteResult(item)} />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modales */}
      {isCreateModalOpen && (
        <CreateExerciseResultModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          refresh={fetchAssignments}
        />
      )}

      {isEditModalOpen && selectedResult && (
        <EditExerciseResultModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedResult(null);
          }}
          result={selectedResult}
          refresh={fetchAssignments}
        />
      )}
    </div>
  );
};

export default ExerciseAssignmentSection;
